/**
 * Upload in Docs and Task detail (slice 5b spec §6.1): each picked file
 * uploaded, then made a file doc (linked to the task, filed in the Area).
 * One file picked opens its doc once it's made; several stay listed, each
 * with its progress, then Open, or why it failed.
 */
import { Button } from "@crew/ui/components/button";
import { Link } from "@crew/ui/components/link";
import { Spinner } from "@crew/ui/components/spinner";
import { cn } from "@crew/ui/lib/utils";
import { CircleAlert, Upload as UploadIcon, X } from "lucide-react";
import { useRef, useState, type ComponentProps } from "react";
import { useNavigate } from "react-router-dom";
import type { Uploaded } from "../../app/api";
import { useCreateFileDoc } from "../docs/api";
import { FilePicker } from "./file-picker";
import { kindOf, sizeLabel } from "./kinds";
import { useUploads, type Upload } from "./use-uploads";

/** The most files one Upload takes (spec §6.1). */
const MAX_AT_ONCE = 10;

/** Uploads that become file docs, and which doc each became. */
export function useDocUploads(where: { taskId?: string; areaId?: string | null } = {}) {
  const create = useCreateFileDoc();
  const navigate = useNavigate();
  const [made, setMade] = useState<Record<string, string>>({});
  const last = useRef<string | null>(null);
  const uploads = useUploads(async (file: Uploaded) => {
    const doc = await create.mutateAsync({
      file_id: file.id,
      ...(where.taskId ? { task_id: where.taskId } : {}),
      ...(where.areaId ? { area_id: where.areaId } : {}),
    });
    last.current = doc.id;
    setMade((m) => ({ ...m, [file.id]: doc.id }));
  });
  // Picks still uploading: two picks before the list updates aren't each "one file" (Devin on #435).
  const picking = useRef(0);
  const [problem, setProblem] = useState<string | null>(null);
  const pick = (files: File[]) => {
    // At most 10 at once (spec §6.1).
    if (files.length > MAX_AT_ONCE) {
      setProblem(`Upload ${MAX_AT_ONCE} files at a time at most.`);
      return;
    }
    setProblem(null);
    const one = files.length === 1 && uploads.list.length === 0 && picking.current === 0;
    picking.current += 1;
    last.current = null;
    void uploads.add(files).then(() => {
      picking.current -= 1;
      // One file, made a doc: open it, and leave nothing behind in the tray.
      if (!one || !last.current || picking.current > 0) return;
      uploads.clear();
      void navigate(`/docs/${last.current}`);
    });
  };
  return { uploads, made, pick, problem };
}

/** The Upload button: the system's picker, several files at once. */
export function UploadButton({
  onFiles,
  label = "Upload",
  ...button
}: Omit<ComponentProps<typeof FilePicker>, "children" | "onFiles"> & {
  onFiles: (files: File[]) => void;
  label?: string;
}) {
  return (
    <FilePicker onFiles={onFiles} aria-label={label} {...button}>
      <UploadIcon />
      <span className={cn(button.size?.startsWith("icon") && "sr-only")}>{label}</span>
    </FilePicker>
  );
}

function UploadRow({
  upload,
  docId,
  onDismiss,
}: {
  upload: Upload;
  docId: string | undefined;
  onDismiss: () => void;
}) {
  const kind = kindOf({ mime: upload.mime, name: upload.name });
  const Icon = kind.icon;
  return (
    <li
      data-upload={upload.state}
      className="flex items-center gap-3 rounded-lg bg-card px-3 py-2.5 shadow-ring"
    >
      <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
        {upload.state === "uploading" ? (
          <Spinner className="size-4" />
        ) : (
          <Icon className="size-4" />
        )}
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-medium text-foreground">{upload.name}</span>
        <span
          className={cn(
            "truncate text-meta",
            upload.state === "failed" ? "text-brick" : "text-muted-foreground",
          )}
        >
          {upload.state === "failed" ? (
            <>
              <CircleAlert aria-hidden className="mr-1 inline size-3 align-[-2px]" />
              {upload.hint}
            </>
          ) : upload.state === "uploading" ? (
            `Uploading · ${sizeLabel(upload.size)}`
          ) : (
            `${kind.label} · ${sizeLabel(upload.size)}`
          )}
        </span>
      </span>
      {upload.state === "done" && docId ? (
        <Link href={`/docs/${docId}`} className="shrink-0 text-sm font-medium underline">
          Open
        </Link>
      ) : null}
      {upload.state !== "uploading" ? (
        <Button size="icon-sm" variant="ghost" aria-label="Dismiss" onClick={onDismiss}>
          <X />
        </Button>
      ) : null}
    </li>
  );
}

/** The uploads in progress, done or failed; nothing with none. */
export function UploadTray({ docs }: { docs: ReturnType<typeof useDocUploads> }) {
  const { uploads, made, problem } = docs;
  if (problem) {
    return (
      <p role="alert" data-upload-problem className="text-meta text-brick">
        {problem}
      </p>
    );
  }
  if (uploads.list.length === 0) return null;
  return (
    <ul data-upload-tray aria-label="Uploads" className="flex flex-col gap-2">
      {uploads.list.map((u) => (
        <UploadRow
          key={u.key}
          upload={u}
          docId={u.file ? made[u.file.id] : undefined}
          onDismiss={() => uploads.remove(u.key)}
        />
      ))}
    </ul>
  );
}
